import {
  type Dispatch,
  type Reducer,
  startTransition,
  useEffect,
  useState,
} from 'react';

import { createAbortError, isAbortError } from '@studio/utils/abortError';
import deepEqual from '@studio/utils/deepEqual';
import {
  type RequestContext,
  useRequestContext_UNSAFE,
} from '@studio/utils/requestContext';
import useBroadcastChannel from '@studio/utils/useBroadcastChannel';
import useEventCallback from '@studio/utils/useEventCallback';

export async function waitUntilVisible(signal: AbortSignal): Promise<void> {
  signal.throwIfAborted();

  if (!document.hidden) {
    return;
  }

  const { resolve, reject, promise } = Promise.withResolvers<void>();

  const handleAbort = () => {
    reject(signal.reason ?? createAbortError());
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) {
      resolve();
    }
  };

  signal.addEventListener('abort', handleAbort);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  try {
    await promise;
  } finally {
    signal.removeEventListener('abort', handleAbort);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
}

export function createStateMachineReducer<
  State extends { readonly status: string },
  Action extends { readonly type: string },
>(transitionMap: {
  [Status in State['status']]?: {
    [Type in Action['type']]?: (
      state: Extract<State, { readonly status: Status }>,
      action: Extract<Action, { readonly type: Type }>,
    ) => State;
  };
}): Reducer<State, Action> {
  // Ensure no prototype is set on the handlers
  Object.setPrototypeOf(transitionMap, null);
  for (const status in transitionMap) {
    const handlers = transitionMap[status as State['status']];
    if (handlers != null) {
      Object.setPrototypeOf(handlers, null);
    }
  }

  return function stateMachineReducer(state, action) {
    const nextState = // @ts-expect-error - generics...
      transitionMap[state.status]?.[action.type]?.(state, action) ?? state;

    if (deepEqual(state, nextState)) {
      return state;
    }

    return nextState;
  };
}

export function makeStateMachineEffects<
  State extends { readonly status: string },
  Action extends { readonly type: string },
>(stateHandlers: {
  [Status in State['status']]?: (
    context: RequestContext,
    state: Extract<State, { readonly status: Status }>,
    dispatch: Dispatch<Action>,
  ) => PromiseLike<Action | undefined | void>;
}) {
  // Ensure no prototype is set on the handlers
  Object.setPrototypeOf(stateHandlers, null);

  return function useStateMachineEffects(
    state: State,
    dispatch: Dispatch<Action>,
  ): void {
    const context = useRequestContext_UNSAFE();
    const [error, setError] = useState<unknown>();

    // Bubble unhandled errors
    if (error !== undefined) {
      throw error;
    }

    useEffect(() => {
      const handler = stateHandlers[state.status as State['status']];

      if (handler === undefined) {
        return undefined;
      }

      let abortController: AbortController;

      const invokeHandler = async () => {
        abortController = new AbortController();

        const signal = AbortSignal.any([
          context.signal,
          abortController.signal,
        ]);

        try {
          const maybeAction = await handler(
            { ...context, signal },
            state as any,
            dispatch,
          );
          signal.throwIfAborted();
          if (maybeAction !== undefined) {
            startTransition(() => {
              dispatch(maybeAction);
            });
          }
        } catch (requestError) {
          if (!isAbortError(requestError, signal)) {
            setError(requestError);
          }
        }
      };

      // eslint-disable-next-line @studio/no-floating-disposables
      const handle = setImmediate(invokeHandler);

      return () => {
        clearImmediate(handle);
        abortController?.abort(
          createAbortError(
            'signal is aborted due to a state transition in useStateMachineEffects',
          ),
        );
      };
    }, [state, dispatch, context]);
  };
}

// Cross-tab action sync
export function useSynchronizedDispatch<Action extends NonNullable<unknown>>(
  dispatch: Dispatch<Action>,
  channelName: string,
  encodeAction: (action: Action) => NonNullable<unknown> | null,
  decodeAction: (action: unknown) => Action | null,
): Dispatch<Action> {
  const { broadcast } = useBroadcastChannel({
    channelName,
    onMessage(action) {
      const decodedAction = decodeAction(action);
      if (decodedAction !== null) {
        dispatch(decodedAction);
      }
    },
  });

  const wrappedDispatch = useEventCallback<Dispatch<Action>>(action => {
    dispatch(action);

    const encodedAction = encodeAction(action);
    if (encodedAction !== null) {
      broadcast(encodedAction);
    }
  });

  return wrappedDispatch;
}
